/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Star } from 'lucide-react';

/**
 * A one-time nudge toward "My Day" — plan.md 1.3.
 *
 * **The feature already shipped and nobody uses it.** This is not new
 * functionality, only awareness: a short explanation, once a day, of what
 * "Add to My Day" already does on every task. No build was asked for beyond
 * this — the acceptance criterion is usage, checked back in a month, not
 * anything this dialog itself can measure.
 *
 * **Once per browser per day, not once ever.** A dismissed dialog people never
 * see again is easy to miss entirely if that first day was a busy one;
 * reappearing once a day for the length of the rollout is still one interruption
 * at most per session, not a nag on every load.
 *
 * **Gone after `PROMO_ENDS`.** This is a rollout push with a stated end, not a
 * permanent onboarding step — leaving the check in past that date would mean a
 * feature "promotion" nobody remembers deciding to keep. The date is a literal
 * for the same reason `ENCRYPTION_KEY` is not: there is nothing to configure
 * per environment, only a decision to encode once.
 */
export const STORAGE_KEY = 'myDayPromoLastShown';
export const PROMO_ENDS = '2026-09-15';

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The whole decision, as a pure function of three strings — so the calendar
 * cutoff and the once-a-day rule can each be asserted directly, without
 * mocking `Date` or a browser to get at them.
 */
export function shouldShowPromo(today: string, lastShown: string, endsAt: string = PROMO_ENDS): boolean {
  return today <= endsAt && lastShown !== today;
}

export function MyDayPromo() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const today = todayISO();

    // Private browsing or a locked-down profile can throw on either call —
    // fails safe by simply not nagging, never by crashing the shell every
    // single load.
    let lastShown = '';
    try {
      lastShown = localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return;
    }
    if (!shouldShowPromo(today, lastShown)) return;

    setOpen(true);
    try {
      localStorage.setItem(STORAGE_KEY, today);
    } catch {
      // Nothing to fall back to — worst case this shows again tomorrow too.
    }
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="motion-backdrop fixed inset-0 z-40 bg-black/30" />
        <Dialog.Content
          id="my-day-promo"
          className="motion-sheet fixed left-1/2 top-1/2 z-50 w-[min(420px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface p-6 shadow-overlay outline-none"
        >
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-action-bg text-action">
              <Star className="size-4 fill-current" aria-hidden />
            </span>
            <Dialog.Title className="m-0 text-lg font-semibold text-ink">Try My Day</Dialog.Title>
          </div>
          <Dialog.Description className="m-0 mb-5 text-sm leading-relaxed text-ink-muted">
            My Day is your own private task list, pulled from every circle you’re in — nobody else
            sees it. Open any task and click <strong className="text-ink">“Add to My Day”</strong> to
            build a personal list for what you’re working on today, separate from the shared board.
          </Dialog.Description>
          <div className="flex justify-end">
            <Dialog.Close className="btn btn-primary">Got it</Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
